// Shared party-picker reads (customers / exporters / transporters / onward
// DCs) — extracted from token-identical clones in Dispatch, Consignments,
// TransportTab, Pallets and PalletDetail during the 2026-07-10 unscoped-
// party-reads sweep. All are tenant-scoped on the client AND tenant-keyed:
// platform admins bypass RLS (an unscoped read mixes every tenant's rows
// into pickers) and an un-keyed cache served the previous tenant's list
// after a switch.
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { fetchAllPaged } from '@/lib/fetchAllPaged'

interface CustomerOpt {
  customer_id: string; customer_code: string | null; name: string
  default_channel: 'EXPORT' | 'LOCAL' | null
}
interface ExporterOpt {
  exporter_id: string; exporter_code: string | null; name: string
  is_default: boolean | null; default_load_port: string | null
}
/** An exporter's receiver (buyer master): what the order and invoice pickers fill from. */
export interface ReceiverOpt {
  receiver_id: string; name: string; address: string | null; client_code: string | null
  default_currency: string; default_incoterms: string | null; default_payment_terms: string | null
  /** The receiver's next commercial-invoice number (with client_code) — Exporter Config's sample. */
  next_invoice_seq?: number | null
}
interface OnwardDcOpt { customer_id: string; customer_code: string | null; name: string }
interface TransporterOpt { transporter_id: string; transporter_code: string | null; name: string }
interface OwnerPartyOpt { grower_id: string; name: string }
interface DeliveryPointOpt {
  delivery_point_id: string; code: string | null; name: string
}

/** Active (non-deleted) customers for dispatch/consignment pickers. */
export function useCustomersActive() {
  const { data: activeTenant } = useActiveTenant()
  const tenantId = activeTenant?.tenantId ?? null
  return useQuery({
    queryKey: ['customers-active', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.from('customers')
        .select('customer_id, customer_code, name, default_channel')
        .eq('tenant_id', tenantId)
        .is('deleted_at', null).order('name')
      if (error) throw error
      return (data ?? []) as CustomerOpt[]
    },
  })
}

/** Active exporters for the new-mission / consignment pickers. */
export function useExportersActive() {
  const { data: activeTenant } = useActiveTenant()
  const tenantId = activeTenant?.tenantId ?? null
  return useQuery({
    queryKey: ['exporters-active', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.from('exporters')
        .select('exporter_id, exporter_code, name, is_default, default_load_port')
        .eq('tenant_id', tenantId)
        .is('deleted_at', null).order('name')
      if (error) throw error
      return (data ?? []) as ExporterOpt[]
    },
  })
}

/** Active transporters for mission / recon / rate-card pickers. */
export function useTransportersActive() {
  const { data: activeTenant } = useActiveTenant()
  const tenantId = activeTenant?.tenantId ?? null
  return useQuery({
    queryKey: ['transporters-active-pick', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.from('transporters')
        .select('transporter_id, transporter_code, name')
        .eq('tenant_id', tenantId)
        .is('deleted_at', null).order('name')
      if (error) throw error
      return (data ?? []) as TransporterOpt[]
    },
  })
}

/**
 * Active intake points for the delivery-point picker (mig 20260727090000).
 *
 * COMMODITY-AGNOSTIC on purpose — there is no commodity/flow filter here and
 * there must never be one: a point receives whatever arrives at it (Magalies
 * takes strawberries and blueberries at the same dock on the same day). What
 * fruit it is stays a property of the delivery's commodity, not of the place.
 *
 * A tenant with no points defined returns [] — every caller renders on data
 * presence, so the whole feature stays invisible (and intake unchanged) until
 * an operator defines one.
 */
export function useDeliveryPoints() {
  const { data: activeTenant } = useActiveTenant()
  const tenantId = activeTenant?.tenantId ?? null
  return useQuery({
    queryKey: ['delivery-points-active', tenantId],
    staleTime: 5 * 60_000,
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.from('delivery_points')
        .select('delivery_point_id, code, name')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('sort_order').order('name')
      if (error) throw error
      return (data ?? []) as DeliveryPointOpt[]
    },
  })
}

/** LOCAL customers offered as a pallet's onward DC (end destination). */
export function useOnwardDcOptions() {
  const { data: activeTenant } = useActiveTenant()
  const tenantId = activeTenant?.tenantId ?? null
  return useQuery({
    queryKey: ['pallet-onward-dc-options', tenantId],
    staleTime: 5 * 60_000,
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.from('customers')
        .select('customer_id, customer_code, name')
        .eq('tenant_id', tenantId)
        .eq('default_channel', 'LOCAL').is('deleted_at', null).order('name')
      if (error) throw error
      return (data ?? []) as OnwardDcOpt[]
    },
  })
}

/** Active parties offered as the OWNER of an external pallet receipt — our
 *  growers and third-party storage clients alike (growers are generic parties
 *  in this schema; the grower row is what routes cold-storage billing).
 *
 *  Shared by the capture form and the receipt page's owner correction, so the
 *  two pickers can never offer different lists — and tenant-scoped on the
 *  client, not just keyed: a platform admin bypasses RLS, and an unscoped read
 *  offers every tenant's parties as an owner for this tenant's fruit. */
export function useOwnerPartyOptions() {
  const { data: activeTenant } = useActiveTenant()
  const tenantId = activeTenant?.tenantId ?? null
  return useQuery({
    queryKey: ['growers-owner-options', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      // Paged, not a lone big .range(): a tenant's party list is a few hundred
      // today, and a picker that silently stops at the server's 1,000-row cap
      // would hide exactly the storage client someone needs to select.
      const { rows, error } = await fetchAllPaged<OwnerPartyOpt>((from, to) =>
        supabase.from('growers')
          .select('grower_id, name')
          .eq('tenant_id', tenantId)
          .eq('is_active', true)
          .is('deleted_at', null)
          .order('name').range(from, to))
      if (error) throw new Error(error)
      return rows
    },
  })
}

/** Active (non-deleted) receivers of the active EXPORTER tenant, by name — the
 *  buyer picker on Sales orders and the bill-to picker on Invoices share it, so
 *  a receiver hidden on one is hidden on the other. `enabled` lets a page that
 *  only needs the list in its editor skip the read on its register. */
export function useReceiversActive(enabled = true) {
  const { data: activeTenant } = useActiveTenant()
  const tenantId = activeTenant?.tenantId ?? null
  return useQuery({
    queryKey: ['exporter-receivers-active', tenantId],
    enabled: !!tenantId && enabled,
    queryFn: async () => {
      const { data, error } = await supabase.from('exporter_receivers')
        .select('receiver_id, name, address, client_code, default_currency, default_incoterms, default_payment_terms, next_invoice_seq')
        .is('deleted_at', null).order('name')
      if (error) throw error
      return (data ?? []) as unknown as ReceiverOpt[]
    },
  })
}
